import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/currency';
import {
  fetchPayrollRun, fetchPayrollLineItems,
  createPayrollLineItem, updatePayrollLineItem, deletePayrollLineItem,
  autoGeneratePayrollRun, recalculatePayrollRun,
  approvePayrollRun, postPayrollRunToGL, lockPayrollRun, reversePayrollRun,
  downloadPayslips,
} from '../api';

const STEPS = ['Setup', 'Line Items', 'Review & Approve', 'Post & Lock'];

const STATUS_COLORS = {
  DRAFT: 'bg-gray-200 text-gray-800',
  APPROVED: 'bg-blue-200 text-blue-800',
  POSTED: 'bg-green-200 text-green-800',
  LOCKED: 'bg-purple-200 text-purple-800',
  REVERSED: 'bg-red-200 text-red-800',
};

const LINE_TYPES = [
  { value: 'SALARY', label: 'Salary' },
  { value: 'BONUS', label: 'Bonus' },
  { value: 'OTHER_ALLOWANCE', label: 'Other Allowance' },
  { value: 'PAYE', label: 'PAYE Tax' },
  { value: 'PENSION_EMPLOYEE', label: 'Pension (Employee)' },
  { value: 'PENSION_EMPLOYER', label: 'Pension (Employer)' },
  { value: 'MEDICAL_AID', label: 'Medical Aid' },
  { value: 'OTHER_DEDUCTION', label: 'Other Deduction' },
];

const PayrollRunWizard = ({ authAxios, addToast, runId, onClose }) => {
  const [step, setStep] = useState(0);
  const [run, setRun] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [newItem, setNewItem] = useState({ line_type: 'SALARY', amount: '', description: '', staff_profile: '' });

  const loadRun = useCallback(async () => {
    try {
      const data = await fetchPayrollRun(authAxios, runId);
      setRun(data);
    } catch {
      addToast('Failed to load payroll run', 'error');
    }
  }, [authAxios, runId, addToast]);

  const loadItems = useCallback(async () => {
    try {
      const data = await fetchPayrollLineItems(authAxios, { payroll_run: runId });
      setLineItems(data.results || data);
    } catch {
      addToast('Failed to load line items', 'error');
    }
  }, [authAxios, runId, addToast]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadRun(), loadItems()]).finally(() => setLoading(false));
  }, [loadRun, loadItems]);

  const doAction = async (fn, successMsg, ...args) => {
    setActionLoading(true);
    try {
      await fn(authAxios, runId, ...args);
      addToast(successMsg, 'success');
      await loadRun();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || 'Action failed';
      addToast(msg, 'error');
    }
    setActionLoading(false);
  };

  const handleAddItem = async () => {
    if (!newItem.amount) return;
    try {
      await createPayrollLineItem(authAxios, { ...newItem, payroll_run: runId });
      addToast('Line item added', 'success');
      setNewItem({ line_type: 'SALARY', amount: '', description: '', staff_profile: '' });
      await loadItems();
      await loadRun();
    } catch {
      addToast('Failed to add line item', 'error');
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await deletePayrollLineItem(authAxios, id);
      await loadItems();
      await loadRun();
    } catch {
      addToast('Failed to delete item', 'error');
    }
  };

  const handleDownload = async () => {
    try {
      const resp = await downloadPayslips(authAxios, runId);
      const blob = resp.data || resp;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslips_run_${runId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      addToast('Failed to download payslips', 'error');
    }
  };

  if (loading || !run) return <div className="p-8 text-center">Loading...</div>;

  const isReadOnly = ['LOCKED', 'REVERSED'].includes(run.status);

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b">
        <div>
          <h2 className="text-xl font-bold">Payroll Run #{run.id}</h2>
          <p className="text-sm text-gray-600">{run.pay_period_start} — {run.pay_period_end} {run.description && `· ${run.description}`}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[run.status] || 'bg-gray-200'}`}>
            {run.status}
          </span>
          {onClose && (
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl">&times;</button>
          )}
        </div>
      </div>

      {/* Steps */}
      <div className="flex border-b">
        {STEPS.map((label, i) => (
          <button
            key={i}
            onClick={() => setStep(i)}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              i === step ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {i + 1}. {label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {/* Step 0 – Setup */}
        {step === 0 && (
          <div>
            <h3 className="font-semibold mb-4">Run Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Total Gross</div>
                <div className="text-lg font-bold">{formatCurrency(run.total_gross || 0)}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Total Deductions</div>
                <div className="text-lg font-bold">{formatCurrency(run.total_deductions || 0)}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Total Net Pay</div>
                <div className="text-lg font-bold text-green-700">{formatCurrency(run.total_net_pay || 0)}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Line Items</div>
                <div className="text-lg font-bold">{lineItems.length}</div>
              </div>
            </div>
            {run.status === 'DRAFT' && (
              <div className="flex gap-2">
                <button
                  disabled={actionLoading}
                  onClick={() => doAction(autoGeneratePayrollRun, 'Auto-generated successfully')}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Auto Generate
                </button>
                <button
                  disabled={actionLoading}
                  onClick={() => doAction(recalculatePayrollRun, 'Recalculated')}
                  className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
                >
                  Recalculate
                </button>
              </div>
            )}
            {run.reversed_at && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm">
                <strong>Reversed</strong> on {new Date(run.reversed_at).toLocaleDateString()} by {run.reversed_by_username || 'unknown'}
              </div>
            )}
          </div>
        )}

        {/* Step 1 – Line Items */}
        {step === 1 && (
          <div>
            <h3 className="font-semibold mb-4">Line Items</h3>
            {!isReadOnly && run.status === 'DRAFT' && (
              <div className="flex gap-2 items-end mb-4 bg-gray-50 p-3 rounded">
                <div>
                  <label className="block text-xs text-gray-600">Type</label>
                  <select className="border rounded p-1.5 text-sm" value={newItem.line_type} onChange={e => setNewItem({ ...newItem, line_type: e.target.value })}>
                    {LINE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600">Amount</label>
                  <input type="number" step="0.01" className="border rounded p-1.5 text-sm w-28" value={newItem.amount} onChange={e => setNewItem({ ...newItem, amount: e.target.value })} />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600">Description</label>
                  <input type="text" className="border rounded p-1.5 text-sm w-full" value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} />
                </div>
                <button onClick={handleAddItem} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Add</button>
              </div>
            )}
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border-b p-2 text-left">Type</th>
                  <th className="border-b p-2 text-left">Description</th>
                  <th className="border-b p-2 text-right">Amount</th>
                  {!isReadOnly && run.status === 'DRAFT' && <th className="border-b p-2 w-20"></th>}
                </tr>
              </thead>
              <tbody>
                {lineItems.map(item => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{item.line_type_display || item.line_type}</td>
                    <td className="p-2">{item.description || '—'}</td>
                    <td className="p-2 text-right">{formatCurrency(item.amount || 0)}</td>
                    {!isReadOnly && run.status === 'DRAFT' && (
                      <td className="p-2">
                        <button onClick={() => handleDeleteItem(item.id)} className="text-red-500 hover:text-red-700 text-xs">Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
                {lineItems.length === 0 && (
                  <tr><td colSpan="4" className="p-4 text-center text-gray-500">No line items yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Step 2 – Review & Approve */}
        {step === 2 && (
          <div>
            <h3 className="font-semibold mb-4">Review & Approve</h3>
            <div className="bg-gray-50 rounded p-4 mb-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><span className="text-gray-500">Period:</span> {run.pay_period_start} to {run.pay_period_end}</div>
                <div><span className="text-gray-500">Status:</span> {run.status}</div>
                <div><span className="text-gray-500">Items:</span> {lineItems.length}</div>
                <div><span className="text-gray-500">Gross:</span> {formatCurrency(run.total_gross || 0)}</div>
                <div><span className="text-gray-500">Deductions:</span> {formatCurrency(run.total_deductions || 0)}</div>
                <div><span className="text-gray-500 font-bold">Net Pay:</span> <span className="font-bold">{formatCurrency(run.total_net_pay || 0)}</span></div>
              </div>
            </div>
            <div className="flex gap-2">
              {run.status === 'DRAFT' && (
                <button
                  disabled={actionLoading}
                  onClick={() => doAction(approvePayrollRun, 'Approved successfully')}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Approve
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3 – Post & Lock */}
        {step === 3 && (
          <div>
            <h3 className="font-semibold mb-4">Post & Lock</h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {run.status === 'APPROVED' && (
                <button
                  disabled={actionLoading}
                  onClick={() => doAction(postPayrollRunToGL, 'Posted to General Ledger')}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Post to GL
                </button>
              )}
              {run.status === 'POSTED' && (
                <>
                  <button
                    disabled={actionLoading}
                    onClick={() => doAction(lockPayrollRun, 'Run locked')}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                  >
                    Lock
                  </button>
                  <button
                    disabled={actionLoading}
                    onClick={() => { if (window.confirm('Reverse this payroll run? An offsetting journal entry will be created.')) doAction(reversePayrollRun, 'Reversed — offsetting journal created'); }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    Reverse
                  </button>
                </>
              )}
              {run.status === 'LOCKED' && (
                <button
                  disabled={actionLoading}
                  onClick={() => { if (window.confirm('Reverse this locked payroll run?')) doAction(reversePayrollRun, 'Reversed — offsetting journal created'); }}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Reverse
                </button>
              )}
              {['POSTED', 'LOCKED', 'APPROVED'].includes(run.status) && (
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Download Payslips (PDF)
                </button>
              )}
            </div>
            {run.reversed_at && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm">
                <strong>This run was reversed</strong> on {new Date(run.reversed_at).toLocaleDateString()}.
                {run.reversal_journal && <span> Reversal journal entry #{run.reversal_journal}.</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollRunWizard;
